import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/state/AppContext';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { search as searchApi } from '@/lib/api';
import { imgSrc } from '@/lib/img';
import { inr } from '@/lib/format';

export const SearchPalette = () => {
  const { searchOpen, setSearchOpen } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!searchOpen) return;
    setQ('');
    setResults([]);
  }, [searchOpen]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchApi(q);
        if (!cancelled) setResults(data.products || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 80);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const go = (slug) => {
    setSearchOpen(false);
    navigate(`/product/${slug}`);
  };

  return (
    <CommandDialog open={searchOpen} onOpenChange={setSearchOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Search Choco Pops, Snaps, Digits…"
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList data-testid="search-results">
        {!q && (
          <CommandGroup heading="Quick links">
            <CommandItem onSelect={() => { setSearchOpen(false); navigate('/shop?category=chocopops'); }}>
              3D Choco Pops
            </CommandItem>
            <CommandItem onSelect={() => { setSearchOpen(false); navigate('/shop?category=chocosnaps'); }}>
              Choco Snaps (customizable)
            </CommandItem>
            <CommandItem onSelect={() => { setSearchOpen(false); navigate('/shop?category=chocodigits'); }}>
              Choco Digits (0–9)
            </CommandItem>
            <CommandItem onSelect={() => { setSearchOpen(false); navigate('/collections'); }}>
              Collections
            </CommandItem>
          </CommandGroup>
        )}
        {q && results.length === 0 && !loading && (
          <CommandEmpty>No matches. Try “unicorn” or “digit 5”.</CommandEmpty>
        )}
        {results.length > 0 && (
          <CommandGroup heading="Products">
            {results.map((p) => (
              <CommandItem
                key={p.slug}
                onSelect={() => go(p.slug)}
                className="flex items-center gap-3"
                data-testid={`search-result-${p.slug}`}
              >
                <img src={imgSrc(p.image, 'avatar')} alt={p.name} className="w-10 h-10 rounded-md object-cover bg-[var(--bb-sky-soft)]" loading="lazy" decoding="async" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.category_label}</div>
                </div>
                <div className="text-sm font-semibold">{inr(p.price)}</div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};
